import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PhotoProfile.css';
import { useNavigate, useParams } from 'react-router-dom';
import { 
    doc, 
    getDoc, 
    setDoc,
    deleteDoc,
    collection, 
    query, 
    where, 
    getDocs, 
    limit, 
    addDoc, 
    serverTimestamp,
    orderBy,
    updateDoc,
    increment,
    Timestamp
} from 'firebase/firestore';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Local Storage Helper
const LocalStorageHelper = {
    saveLoginData: (userId, email, name, userType = 'user') => {
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userId', userId);
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userName', name);
        localStorage.setItem('userType', userType);
    },

    isLoggedIn: () => {
        return localStorage.getItem('isLoggedIn') === 'true';
    },

    getLoginData: () => {
        return {
            userId: localStorage.getItem('userId'),
            email: localStorage.getItem('userEmail'),
            name: localStorage.getItem('userName'),
            userType: localStorage.getItem('userType'),
        };
    },

    clearLoginData: () => {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userId');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
        localStorage.removeItem('userType');
    }
};

const PhotoProfile = () => {
    const navigate = useNavigate();
    const { photographerId } = useParams();
    
    // State for photographer data
    const [photographerData, setPhotographerData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [albums, setAlbums] = useState([]);
    const [isLoadingAlbums, setIsLoadingAlbums] = useState(true);
    const [selectedAlbum, setSelectedAlbum] = useState(null);
    const [albumPhotos, setAlbumPhotos] = useState([]);
    const [showAlbumModal, setShowAlbumModal] = useState(false);
    const [rating, setRating] = useState(0);
    const [reviewText, setReviewText] = useState('');
    const [relatedPhotographers, setRelatedPhotographers] = useState([]);

    // Authentication state
    const [logged_in, setLogged_in] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const profileWidgetRef = useRef(null);

    // Reviews state
    const [reviews, setReviews] = useState([]);
    const [isLoadingReviews, setIsLoadingReviews] = useState(true);
    const [isSubmittingReview, setIsSubmittingReview] = useState(false);
    const [userHasReviewed, setUserHasReviewed] = useState(false);
    const [hoveredRating, setHoveredRating] = useState(0);

    // Favorites state
    const [isFavorited, setIsFavorited] = useState(false);
    const [isUpdatingFavorite, setIsUpdatingFavorite] = useState(false);

    // Enquiry Modal State
    const [showEnquiryModal, setShowEnquiryModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [enquiryForm, setEnquiryForm] = useState({
        location: '',
        preferredDate: new Date(),
        budget: '',
        typeOfShoot: '',
        timeOfDay: '',
        message: ''
    });
    const [isSubmittingEnquiry, setIsSubmittingEnquiry] = useState(false);

    // Helper function to get lowest price
    const getLowestPrice = useCallback((services) => {
        if (!services || Object.keys(services).length === 0) return '25,000';
        
        const prices = Object.values(services).filter(price => price > 0);
        if (prices.length === 0) return '25,000';
        
        const minPrice = Math.min(...prices);
        return minPrice.toLocaleString('en-IN');
    }, []);

    // Get main service category
    const getMainService = useCallback((services) => {
        if (!services || Object.keys(services).length === 0) return 'Photography';
        
        const serviceKeys = Object.keys(services);
        const serviceMap = {
            'Wedding': 'Wedding Photography',
            'Portrait': 'Portrait Photography',
            'Event': 'Event Photography',
            'Fashion': 'Fashion Photography',
            'Product': 'Product Photography',
            'Corporate': 'Corporate Photography',
            'Nature': 'Nature Photography'
        };
        
        for (const key of serviceKeys) {
            if (services[key] > 0 && serviceMap[key]) {
                return serviceMap[key];
            }
        }
        
        return serviceKeys[0] ? `${serviceKeys[0]} Photography` : 'Photography';
    }, []);

    // Enquiry Form Handlers
    const handleEnquiryFormChange = (e) => {
        const { name, value } = e.target;
        setEnquiryForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateChange = (date) => {
        setEnquiryForm(prev => ({
            ...prev,
            preferredDate: date
        }));
    };

    const handleEnquirySubmit = async (e) => {
        e.preventDefault();
        
        if (!logged_in || !userProfile) {
            toast.error('Please log in to send an enquiry');
            navigate('/Login');
            return;
        }

        try {
            setIsSubmittingEnquiry(true);
            
            const enquiryData = {
                userId: userProfile.id,
                userName: userProfile.name,
                userEmail: userProfile.email,
                photographerId: photographerId,
                photographerName: photographerData.name,
                location: enquiryForm.location,
                preferredDate: Timestamp.fromDate(enquiryForm.preferredDate),
                budget: enquiryForm.budget,
                typeOfShoot: enquiryForm.typeOfShoot,
                timeOfDay: enquiryForm.timeOfDay,
                message: enquiryForm.message,
                status: 'pending',
                createdAt: serverTimestamp()
            };

            const requestsRef = collection(db, 'photographers', photographerId, 'requests');
            await addDoc(requestsRef, enquiryData);

            setShowSuccessModal(true);
            setShowEnquiryModal(false);
            
            // Reset form
            setEnquiryForm({
                location: '',
                preferredDate: new Date(),
                budget: '',
                typeOfShoot: '',
                timeOfDay: '',
                message: ''
            });

        } catch (error) {
            console.error('Error sending enquiry:', error);
            toast.error('Error sending enquiry. Please try again.');
        } finally {
            setIsSubmittingEnquiry(false);
        }
    };

    const closeEnquiryModal = () => {
        setShowEnquiryModal(false);
        setEnquiryForm({
            location: '',
            preferredDate: new Date(),
            budget: '',
            typeOfShoot: '',
            timeOfDay: '',
            message: ''
        });
    };

    // Check if photographer is already favorited
    const checkIfFavorited = useCallback(async (userId, photographerId) => {
        try {
            if (!userId || !photographerId) return false;
            
            const favoriteDocRef = doc(db, 'users', userId, 'favorites', photographerId);
            const favoriteDoc = await getDoc(favoriteDocRef);
            
            return favoriteDoc.exists();
        } catch (error) {
            console.error('Error checking favorite status:', error);
            return false;
        }
    }, []);

    // Add photographer to favorites
    const addToFavorites = async (userId, photographerData) => {
        try {
            setIsUpdatingFavorite(true);
            
            const favoriteData = {
                photographerId: photographerData.id,
                photographerName: photographerData.name,
                profileImage: photographerData.profileImage || 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg',
                rating: photographerData.rating || 4.5,
                location: photographerData.address || photographerData.city || 'Location not specified',
                startingPrice: photographerData.startingPrice,
                specialization: getMainService(photographerData.services),
                favoritedAt: serverTimestamp()
            };
            
            const favoriteDocRef = doc(db, 'users', userId, 'favorites', photographerData.id);
            await setDoc(favoriteDocRef, favoriteData);
            
            setIsFavorited(true);
            toast.success('Added to favorites!');
            
        } catch (error) {
            console.error('Error adding to favorites:', error);
            toast.error('Error adding to favorites');
        } finally {
            setIsUpdatingFavorite(false);
        }
    };

    // Remove photographer from favorites
    const removeFromFavorites = async (userId, photographerId) => {
        try {
            setIsUpdatingFavorite(true);
            
            const favoriteDocRef = doc(db, 'users', userId, 'favorites', photographerId);
            await deleteDoc(favoriteDocRef);
            
            setIsFavorited(false);
            toast.success('Removed from favorites!');
            
        } catch (error) {
            console.error('Error removing from favorites:', error);
            toast.error('Error removing from favorites');
        } finally {
            setIsUpdatingFavorite(false);
        }
    };

    // Toggle favorite status
    const handleFavoriteToggle = async () => {
        if (!logged_in || !userProfile) {
            toast.error('Please log in to add favorites');
            navigate('/Login');
            return;
        }
        
        if (isUpdatingFavorite) return;
        
        if (isFavorited) {
            await removeFromFavorites(userProfile.id, photographerId);
        } else {
            await addToFavorites(userProfile.id, photographerData);
        }
    };

    // Get album photos from Firebase Storage
    const getAlbumPhotos = useCallback(async (photographerId, albumName) => {
        try {
            const possiblePaths = [
                `photographers/${photographerId}/albums/${albumName}`,
                `photographers/${photographerId}/Albums/${albumName}`,
                `albums/${photographerId}/${albumName}`,
                `Albums/${photographerId}/${albumName}`,
                `${photographerId}/albums/${albumName}`,
                `${photographerId}/Albums/${albumName}`
            ];
            
            for (const path of possiblePaths) {
                try {
                    const albumRef = ref(storage, path);
                    const albumList = await listAll(albumRef);
                    
                    if (albumList.items.length > 0) {
                        const photoUrls = await Promise.all(
                            albumList.items.map(async (item) => {
                                try {
                                    return await getDownloadURL(item);
                                } catch (error) {
                                    console.error('Error getting download URL:', error);
                                    return null;
                                }
                            })
                        );
                        
                        return photoUrls.filter(url => url !== null);
                    }
                } catch (error) {
                    continue;
                }
            }
            
            return [];
            
        } catch (error) {
            console.error('Error getting album photos:', error);
            return [];
        }
    }, []);

    // Get photos from a storage reference
    const getAlbumPhotosFromRef = useCallback(async (albumRef) => {
        try {
            const photosList = await listAll(albumRef);
            const photoUrls = await Promise.all(
                photosList.items.map(async (item) => {
                    try {
                        return await getDownloadURL(item);
                    } catch (error) {
                        console.error('Error getting photo URL:', error);
                        return null;
                    }
                })
            );
            
            return photoUrls.filter(url => url !== null);
        } catch (error) {
            console.error('Error getting album photos from ref:', error);
            return [];
        }
    }, []);

    // Get albums from Firebase Storage structure
    const getAlbumsFromStorage = useCallback(async (photographerId) => {
        try {
            const possibleBasePaths = [
                `photographers/${photographerId}/albums`,
                `photographers/${photographerId}/Albums`,
                `albums/${photographerId}`,
                `Albums/${photographerId}`,
                `${photographerId}/albums`,
                `${photographerId}/Albums`
            ];
            
            for (const basePath of possibleBasePaths) {
                try {
                    const albumsRef = ref(storage, basePath);
                    const albumsList = await listAll(albumsRef);
                    
                    if (albumsList.prefixes.length > 0) {
                        const albumsData = await Promise.all(
                            albumsList.prefixes.map(async (albumRef, index) => {
                                const albumName = albumRef.name;
                                const photos = await getAlbumPhotosFromRef(albumRef);
                                
                                return {
                                    id: `storage_album_${index}`,
                                    albumName: albumName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                                    description: `${albumName} photography collection`,
                                    location: 'Professional Shoot',
                                    coverImage: photos[0] || 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg',
                                    photoCount: photos.length,
                                    category: 'Portfolio',
                                    photos: photos
                                };
                            })
                        );
                        
                        return albumsData;
                    }
                } catch (error) {
                    continue;
                }
            }
            
            return [];
        } catch (error) {
            console.error('Error getting albums from storage:', error);
            return [];
        }
    }, [getAlbumPhotosFromRef]);

    // Load photographer's albums from Firebase
    const loadPhotographerAlbums = useCallback(async (photographerId) => {
        try {
            setIsLoadingAlbums(true);
            
            const albumsQuery = query(
                collection(db, 'albums'),
                where('photographerId', '==', photographerId)
            );
            
            const albumSnapshot = await getDocs(albumsQuery);
            const albumsData = [];
            
            if (!albumSnapshot.empty) {
                for (const albumDoc of albumSnapshot.docs) {
                    const albumData = albumDoc.data();
                    
                    const photos = await getAlbumPhotos(photographerId, albumData.albumName || albumDoc.id);
                    
                    albumsData.push({
                        id: albumDoc.id,
                        albumName: albumData.albumName || albumData.title || 'Untitled Album',
                        description: albumData.description || 'Photography collection',
                        location: albumData.location || 'Various locations',
                        createdAt: albumData.createdAt,
                        coverImage: albumData.coverImage || photos[0] || 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg',
                        photoCount: photos.length,
                        category: albumData.category || 'General',
                        photos: photos
                    });
                }
            } else {
                const photographerDoc = await getDoc(doc(db, 'photographers', photographerId));
                
                if (photographerDoc.exists()) {
                    const photographerData = photographerDoc.data();
                    const photographerAlbums = photographerData.albums || [];
                    
                    for (const album of photographerAlbums) {
                        const photos = await getAlbumPhotos(photographerId, album.albumName);
                        
                        albumsData.push({
                            id: album.albumName || `album_${albumsData.length}`,
                            albumName: album.albumName || album.title || 'Untitled Album',
                            description: album.description || 'Photography collection',
                            location: album.location || 'Various locations',
                            createdAt: album.createdAt,
                            coverImage: album.coverImage || photos[0] || 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg',
                            photoCount: photos.length,
                            category: album.category || 'General',
                            photos: photos
                        });
                    }
                }
            }
            
            if (albumsData.length === 0) {
                const storageAlbums = await getAlbumsFromStorage(photographerId);
                albumsData.push(...storageAlbums);
            }
            
            setAlbums(albumsData);
            
        } catch (error) {
            console.error('Error loading albums:', error);
            setAlbums([]);
        } finally {
            setIsLoadingAlbums(false);
        }
    }, [getAlbumPhotos, getAlbumsFromStorage]);

    // Load related photographers
    const loadRelatedPhotographers = useCallback(async (location) => {
        try {
            const q = query(
                collection(db, 'photographers'),
                where('address', '==', location),
                limit(4)
            );
            const querySnapshot = await getDocs(q);
            const related = [];
            
            querySnapshot.forEach((doc) => {
                if (doc.id !== photographerId) {
                    const data = doc.data();
                    related.push({
                        id: doc.id,
                        name: data.name,
                        location: data.address || data.city,
                        price: getLowestPrice(data.serviceableCategoriesWithPrices || data.serviceCategories || {}),
                        rating: data.rating || 4.5,
                        image: data.profileImage || 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg'
                    });
                }
            });
            
            setRelatedPhotographers(related);
        } catch (error) {
            console.error('Error loading related photographers:', error);
        }
    }, [photographerId, getLowestPrice]);

    // Load reviews from Firebase
    const loadReviews = useCallback(async (photographerId) => {
        try {
            setIsLoadingReviews(true);

            const reviewsQuery = query(
                collection(db, 'reviews', photographerId, 'reviews'),
                orderBy('createdAt', 'desc')
            );

            const reviewsSnapshot = await getDocs(reviewsQuery);
            const reviewsData = [];

            reviewsSnapshot.forEach((doc) => {
                const reviewData = doc.data();
                reviewsData.push({
                    id: doc.id,
                    ...reviewData,
                    createdAt: reviewData.createdAt?.toDate() || new Date()
                });
            });

            setReviews(reviewsData);

            if (userProfile?.id) {
                const userReview = reviewsData.find(review => review.userId === userProfile.id);
                setUserHasReviewed(!!userReview);
            }

        } catch (error) {
            console.error('Error loading reviews:', error);
            setReviews([]);
        } finally {
            setIsLoadingReviews(false);
        }
    }, [userProfile?.id]);

    // Submit review to Firebase
    const handleReviewSubmit = async (e) => {
        e.preventDefault();

        if (!logged_in || !userProfile) {
            toast.error('Please log in to submit a review');
            navigate('/Login');
            return;
        }

        if (rating === 0 || rating < 1 || rating > 5) {
            toast.error('Please select a rating between 1-5 stars');
            return;
        }

        if (!reviewText.trim()) {
            toast.error('Please write a review');
            return;
        }

        if (userHasReviewed) {
            toast.error('You have already reviewed this photographer');
            return;
        }

        try {
            setIsSubmittingReview(true);

            const reviewData = {
                userId: userProfile.id,
                userName: userProfile.name,
                userEmail: userProfile.email,
                rating: Number(rating),
                reviewText: reviewText.trim(),
                createdAt: serverTimestamp()
            };

            const reviewsSubcollectionRef = collection(db, 'reviews', photographerId, 'reviews');
            const reviewRef = await addDoc(reviewsSubcollectionRef, reviewData);

            const newReviewCount = reviews.length + 1;
            const currentTotalRating = (photographerData.rating || 0) * (photographerData.reviewsCount || 0);
            const newTotalRating = currentTotalRating + Number(rating);
            const newAverageRating = newTotalRating / newReviewCount;

            const photographerRef = doc(db, 'photographers', photographerId);

            await updateDoc(photographerRef, {
                rating: parseFloat(newAverageRating.toFixed(1)),
                reviewsCount: increment(1)
            });

            setPhotographerData(prev => ({
                ...prev,
                rating: parseFloat(newAverageRating.toFixed(1)),
                reviewsCount: newReviewCount
            }));

            setRating(0);
            setReviewText('');
            setHoveredRating(0);
            setUserHasReviewed(true);

            await loadReviews(photographerId);

            toast.success('Review submitted successfully!');

        } catch (error) {
            console.error('Error submitting review:', error);
            toast.error('Error submitting review. Please try again.');
        } finally {
            setIsSubmittingReview(false);
        }
    };

    // Enhanced star rating handlers
    const handleStarClick = (starValue) => {
        setRating(starValue);
    };

    const handleStarHover = (starValue) => {
        setHoveredRating(starValue);
    };

    const handleStarLeave = () => {
        setHoveredRating(0);
    };

    // Enhanced star rendering with hover effects
    const renderInteractiveStars = (currentRating, isInteractive = false) => {
        const stars = [];
        const displayRating = hoveredRating || currentRating;
        
        for (let i = 1; i <= 5; i++) {
            const isFilled = i <= displayRating;
            const isHovered = hoveredRating > 0 && i <= hoveredRating;
            
            stars.push(
                <i
                    key={i}
                    className={`${isFilled ? 'fas' : 'far'} fa-star ${isInteractive ? 'interactive-star' : ''} ${isHovered ? 'hovered-star' : ''}`}
                    onClick={isInteractive ? () => handleStarClick(i) : undefined}
                    onMouseEnter={isInteractive ? () => handleStarHover(i) : undefined}
                    onMouseLeave={isInteractive ? handleStarLeave : undefined}
                    style={{
                        cursor: isInteractive ? 'pointer' : 'default',
                        color: isFilled ? '#ffd700' : '#ddd',
                        fontSize: isInteractive ? '1.5rem' : '1rem',
                        margin: '0 2px',
                        transition: 'all 0.2s ease'
                    }}
                />
            );
        }
        
        return stars;
    };

    // Format review date
    const formatReviewDate = (date) => {
        if (!date) return 'Recently';
        
        const now = new Date();
        const reviewDate = date instanceof Date ? date : new Date(date);
        const diffInHours = Math.floor((now - reviewDate) / (1000 * 60 * 60));
        
        if (diffInHours < 1) return 'Just now';
        if (diffInHours < 24) return `${diffInHours} hours ago`;
        if (diffInHours < 48) return 'Yesterday';
        if (diffInHours < 168) return `${Math.floor(diffInHours / 24)} days ago`;
        
        return reviewDate.toLocaleDateString();
    };

    const renderStars = (currentRating) => {
        return Array.from({ length: 5 }, (_, index) => (
            <i
                key={index}
                className={`${index < currentRating ? 'fas' : 'far'} fa-star`}
                style={{ color: '#ffd700', margin: '0 2px' }}
            />
        ));
    };

    // Firebase Authentication Check
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setAuthLoading(true);
            
            if (user) {
                try {
                    const loginData = LocalStorageHelper.getLoginData();
                    let userType = loginData.userType || 'user';
                    
                    let userDocRef = doc(db, userType === 'vendor' || userType === 'photographer' ? 'photographers' : 'users', user.uid);
                    let userDoc = await getDoc(userDocRef);
                    
                    if (!userDoc.exists()) {
                        userType = userType === 'vendor' || userType === 'photographer' ? 'user' : 'photographer';
                        userDocRef = doc(db, userType === 'photographer' ? 'photographers' : 'users', user.uid);
                        userDoc = await getDoc(userDocRef);
                        
                        if (userDoc.exists()) {
                            LocalStorageHelper.saveLoginData(
                                user.uid,
                                user.email,
                                user.displayName || "User",
                                userType
                            );
                        }
                    }
                    
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        
                        const getAvatarUrl = (userData, user) => {
                            const avatarSources = [
                                userData.profileImage,
                                userData.avatarUrl,
                                userData.avatar,
                                user.photoURL
                            ];
                            
                            const validAvatar = avatarSources.find(url => url && url.trim() !== '' && url !== 'undefined');
                            
                            if (validAvatar) {
                                return validAvatar;
                            }
                            
                            const name = userData.name || user.displayName || "User";
                            return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`;
                        };
                        
                        const profileData = {
                            id: user.uid,
                            name: userData.name || user.displayName || "User",
                            email: userData.email || user.email,
                            avatar: getAvatarUrl(userData, user),
                            role: userType,
                            joinDate: userData.createdAt,
                            isVerified: user.emailVerified || userData.emailVerified || false,
                            userType: userType
                        };
                        
                        setUserProfile(profileData);
                        setLogged_in(true);
                        
                        LocalStorageHelper.saveLoginData(
                            user.uid,
                            userData.email || user.email,
                            userData.name || user.displayName || "User",
                            userType
                        );
                        
                    } else {
                        const basicProfile = {
                            id: user.uid,
                            name: user.displayName || "User",
                            email: user.email,
                            userType: 'user',
                            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || 'User')}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`,
                            isVerified: user.emailVerified || false
                        };
                        
                        setUserProfile(basicProfile);
                        setLogged_in(true);
                        
                        LocalStorageHelper.saveLoginData(
                            user.uid,
                            user.email,
                            user.displayName || "User",
                            'user'
                        );
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                    const basicProfile = {
                        id: user.uid,
                        name: user.displayName || "User",
                        email: user.email,
                        userType: 'user',
                        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || 'User')}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`,
                        isVerified: user.emailVerified || false
                    };
                    
                    setUserProfile(basicProfile);
                    setLogged_in(true);
                }
            } else {
                setLogged_in(false);
                setUserProfile(null);
                LocalStorageHelper.clearLoginData();
            }
            
            setAuthLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Load photographer data from Firebase
    useEffect(() => {
        const loadPhotographerData = async () => {
            if (!photographerId) return;
            
            setIsLoading(true);
            try {
                const docRef = doc(db, 'photographers', photographerId);
                const docSnap = await getDoc(docRef);
                
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setPhotographerData({
                        id: docSnap.id,
                        ...data,
                        services: data.serviceableCategoriesWithPrices || data.serviceCategories || {},
                        startingPrice: getLowestPrice(data.serviceableCategoriesWithPrices || data.serviceCategories || {}),
                        reviews: data.reviews || [],
                        rating: data.rating || 4.5,
                        reviewsCount: data.reviewsCount || 0,
                        yearsExperience: data.yearsExperience || 1,
                        completedProjects: data.completedProjects || 0,
                        specialties: data.specialties || [],
                        policies: data.policies || {
                            cancellationPolicy: '',
                            paymentTerms: '',
                            travelCostBearer: '',
                            deliveryTime: ''
                        },
                        professionalDetails: data.professionalDetails || {
                            workLocation: '',
                            proficientPosing: false,
                            completedProjects: 0
                        },
                        serviceAreas: data.serviceAreas || data.serviceableAreas || [],
                        serviceCategories: data.serviceCategories || data.serviceableCategoriesWithPrices || {},
                        features: data.features || []
                    });
                    
                    await loadPhotographerAlbums(photographerId);
                    await loadRelatedPhotographers(data.address || data.city);
                    await loadReviews(photographerId);
                } else {
                    toast.error('Photographer not found');
                    navigate('/BrowsePhotographer');
                }
            } catch (error) {
                console.error('Error loading photographer:', error);
                toast.error('Error loading photographer data');
            } finally {
                setIsLoading(false);
            }
        };

        loadPhotographerData();
    }, [photographerId, navigate, getLowestPrice, loadPhotographerAlbums, loadRelatedPhotographers, loadReviews]);

    // Check favorite status when user profile changes
    useEffect(() => {
        const checkFavoriteStatus = async () => {
            if (logged_in && userProfile && photographerId) {
                const favorited = await checkIfFavorited(userProfile.id, photographerId);
                setIsFavorited(favorited);
            }
        };
        
        checkFavoriteStatus();
    }, [logged_in, userProfile, photographerId, checkIfFavorited]);

    // Reload reviews when user profile changes
    useEffect(() => {
        if (photographerId && userProfile) {
            loadReviews(photographerId);
        }
    }, [photographerId, userProfile, loadReviews]);

    // Enhanced Logout Handler
    const handleLogout = useCallback(async () => {
        try {
            await signOut(auth);
            LocalStorageHelper.clearLoginData();
            
            setLogged_in(false);
            setUserProfile(null);
            setShowProfileMenu(false);
        } catch (error) {
            console.error('Error logging out:', error);
        }
    }, []);

    // Handle album click to show photos
    const handleAlbumClick = (album) => {
        setSelectedAlbum(album);
        setAlbumPhotos(album.photos || []);
        setShowAlbumModal(true);
    };

    // Close album modal
    const closeAlbumModal = () => {
        setShowAlbumModal(false);
        setSelectedAlbum(null);
        setAlbumPhotos([]);
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading photographer profile...</p>
            </div>
        );
    }

    // Error state
    if (!photographerData) {
        return (
            <div className="error-container">
                <h2>Photographer not found</h2>
                <button onClick={() => navigate('/BrowsePhotographer')}>
                    Back to Browse
                </button>
            </div>
        );
    }

    return (
        <>
            <link
                href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css"
                rel="stylesheet"
            />
            
            <div className="photo-profile">
                {/* Header */}
                <div className="header">
                    <div className="header-background">
                        <img
                            alt="Header background"
                            className="header-image"
                            src={photographerData.coverImage || photographerData.profileImage || 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg'}
                            onError={(e) => {
                                e.target.src = 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg';
                            }}
                        />
                        <div className="header-overlay"></div>
                    </div>
                    
                    {/* Top Navigation */}
                    <div className="top-nav">
                        <div className="nav-left">
                            <a href="#" className="logo-link" onClick={() => navigate("/LandingPage")}>
                                <i className="fas fa-camera logo-icon"></i>
                            </a>
                            <a href="#" className="nav-link" onClick={() => navigate("/BrowsePhotographer")}>
                                Browse Photographers
                            </a>
                            <a href="#" className="nav-link" onClick={() => navigate("/About")}>
                                About
                            </a>
                        </div>
                        
                        <div className="nav-right">
                            <button className="search-btn">
                                <i className="fas fa-search"></i>
                            </button>
                            {logged_in && userProfile ? (
                                <div className="profile-menu">
                                    <img 
                                        src={userProfile.avatar} 
                                        alt={userProfile.name}
                                        className="profile-avatar"
                                        onClick={() => setShowProfileMenu(!showProfileMenu)}
                                    />
                                    {showProfileMenu && (
                                        <div className="profile-dropdown">
                                            <div className="profile-info">
                                                <span>{userProfile.name}</span>
                                                <span className="profile-email">{userProfile.email}</span>
                                            </div>
                                            <button onClick={handleLogout}>Logout</button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <button className="btn-secondary" onClick={() => navigate("/Login")}>
                                        Log In
                                    </button>
                                    <button className="btn-primary" onClick={() => navigate("/Register")}>
                                        Sign Up
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    
                    {/* Profile Icon */}
                    <div className="profile-icon-container">
                        <img
                            alt="Profile"
                            className="profile-icon"
                            src={photographerData.profileImage || 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg'}
                            onError={(e) => {
                                e.target.src = 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg';
                            }}
                        />
                    </div>
                </div>
                
                {/* Main Content */}
                <div className="main-content">
                    {/* Profile Info */}
                    <div className="profile-info">
                        <div className="profile-details">
                            <h1>{photographerData.name}</h1>
                            <div className="profile-meta">
                                <div className="meta-item">
                                    <i className="fas fa-camera"></i>
                                    <span>{getMainService(photographerData.services)}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="fas fa-map-marker-alt"></i>
                                    <a href="#" className="location-link">
                                        {photographerData.address || photographerData.city || 'Location not specified'}
                                    </a>
                                </div>
                                <div className="meta-item">
                                    <i className="fas fa-star"></i>
                                    <span>{photographerData.rating} ({photographerData.reviewsCount} reviews)</span>
                                </div>
                                <div className="meta-item">
                                    <i className="fas fa-rupee-sign"></i>
                                    <span>Starting from ₹{photographerData.startingPrice}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="profile-actions">
                            <button 
                                className={`btn-follow ${isFavorited ? 'favorited' : ''}`}
                                onClick={handleFavoriteToggle}
                                disabled={isUpdatingFavorite}
                            >
                                <i className={`${isFavorited ? 'fas' : 'far'} fa-heart`}></i>
                                {isFavorited ? 'Favorited' : 'Add to Favorites'}
                            </button>
                            <button 
                                className="btn-message"
                                onClick={() => setShowEnquiryModal(true)}
                            >
                                <i className="fas fa-envelope"></i>
                                Send Enquiry
                            </button>
                        </div>
                    </div>
                    
                    {/* Tabs */}
                    <div className="tabs-nav">
                        <a href="#work" className="tab-link active">Work</a>
                        <a href="#about" className="tab-link">About</a>
                        <a href="#reviews" className="tab-link">Reviews ({reviews.length})</a>
                        <a href="#contact" className="tab-link">Contact</a>
                    </div>
                    
                    {/* Work Gallery */}
                    <div className="work-gallery" id="work">
                        {isLoadingAlbums ? (
                            <div className="loading-albums">
                                <div className="loading-spinner"></div>
                                <p>Loading portfolio...</p>
                            </div>
                        ) : albums.length > 0 ? (
                            albums.map((album) => (
                                <div key={album.id} className="work-item" onClick={() => handleAlbumClick(album)}>
                                    <img
                                        src={album.coverImage}
                                        alt={album.albumName}
                                        className="work-image"
                                        loading="lazy"
                                        decoding="async"
                                        onError={(e) => {
                                            e.target.src = 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg';
                                        }}
                                    />
                                    <div className="work-overlay">
                                        <h3>{album.albumName}</h3>
                                        <p>{album.photoCount} photos</p>
                                        <span className="work-category">{album.category}</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-work">
                                <i className="fas fa-photo-video"></i>
                                <p>No portfolio items available yet</p>
                            </div>
                        )}
                    </div>
                    
                    {/* About Section */}
                    <div className="about-section" id="about">
                        <h2>About {photographerData.name}</h2>
                        <div className="about-content">
                            <p>
                                {photographerData.description || 
                                `${photographerData.name} is an accomplished photographer with ${photographerData.yearsExperience}+ years of experience, 
                                specializing in ${getMainService(photographerData.services).toLowerCase()}. With a keen eye for detail and a creative approach, 
                                ${photographerData.name} captures moments that blend emotion, elegance, and style.`}
                            </p>
                            
                            <div className="about-stats">
                                <div className="stat-item">
                                    <h3>{photographerData.yearsExperience}+</h3>
                                    <p>Years Experience</p>
                                </div>
                                <div className="stat-item">
                                    <h3>{albums.length}</h3>
                                    <p>Portfolio Albums</p>
                                </div>
                                <div className="stat-item">
                                    <h3>{photographerData.reviewsCount}</h3>
                                    <p>Happy Clients</p>
                                </div>
                                <div className="stat-item">
                                    <h3>{photographerData.rating}</h3>
                                    <p>Average Rating</p>
                                </div>
                            </div>
                            
                            {photographerData.serviceAreas && photographerData.serviceAreas.length > 0 && (
                                <div className="service-areas">
                                    <h3>Service Areas</h3>
                                    <div className="service-tags">
                                        {photographerData.serviceAreas.map((area, index) => (
                                            <span key={index} className="service-tag">{area}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {photographerData.serviceCategories && Object.keys(photographerData.serviceCategories).length > 0 && (
                                <div className="service-categories">
                                    <h3>Services Offered</h3>
                                    <div className="service-tags">
                                        {Object.keys(photographerData.serviceCategories).filter(service => 
                                            service.trim() !== '' && photographerData.serviceCategories[service] > 0
                                        ).map((service, index) => (
                                            <span key={index} className="service-tag">{service}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Reviews Section */}
                    <div className="reviews-section" id="reviews">
                        <h2>Reviews ({reviews.length})</h2>
                        
                        {isLoadingReviews ? (
                            <div className="loading-reviews">
                                <div className="loading-spinner"></div>
                                <p>Loading reviews...</p>
                            </div>
                        ) : (
                            <div className="reviews-content">
                                <div className="reviews-list">
                                    {reviews.length > 0 ? (
                                        reviews.slice(0, 5).map((review) => (
                                            <div key={review.id} className="review-item">
                                                <div className="review-header">
                                                    <div className="reviewer-info">
                                                        <h4>{review.userName}</h4>
                                                        <div className="review-rating">
                                                            {renderStars(review.rating)}
                                                        </div>
                                                    </div>
                                                    <span className="review-date">{formatReviewDate(review.createdAt)}</span>
                                                </div>
                                                <p className="review-text">{review.reviewText}</p>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="no-reviews">
                                            <i className="fas fa-star"></i>
                                            <p>No reviews yet. Be the first to review!</p>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Write Review Form */}
                                <div className="write-review">
                                    <h3>Write a Review</h3>
                                    
                                    {!logged_in ? (
                                        <div className="login-prompt">
                                            <p>Please log in to write a review</p>
                                            <button 
                                                className="btn-primary"
                                                onClick={() => navigate('/Login')}
                                            >
                                                Log In to Review
                                            </button>
                                        </div>
                                    ) : userHasReviewed ? (
                                        <div className="already-reviewed">
                                            <i className="fas fa-check-circle"></i>
                                            <p>You have already reviewed this photographer</p>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleReviewSubmit} className="review-form">
                                            <div className="rating-input">
                                                <label>Rating:</label>
                                                <div className="stars-input">
                                                    {renderInteractiveStars(rating, true)}
                                                </div>
                                            </div>
                                            
                                            <div className="review-input">
                                                <label>Your Review:</label>
                                                <textarea 
                                                    placeholder="Tell us about your experience with this photographer..."
                                                    value={reviewText}
                                                    onChange={(e) => setReviewText(e.target.value)}
                                                    required
                                                    maxLength={500}
                                                    rows={4}
                                                />
                                                <div className="character-count">
                                                    {reviewText.length}/500 characters
                                                </div>
                                            </div>
                                            
                                            <button 
                                                type="submit" 
                                                className="btn-primary"
                                                disabled={isSubmittingReview || rating === 0 || !reviewText.trim()}
                                            >
                                                {isSubmittingReview ? (
                                                    <>
                                                        <i className="fas fa-spinner fa-spin"></i>
                                                        Submitting...
                                                    </>
                                                ) : (
                                                    'Submit Review'
                                                )}
                                            </button>
                                        </form>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Enquiry Modal */}
                {showEnquiryModal && (
                    <div className="modal-overlay" onClick={closeEnquiryModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Send Enquiry</h2>
                                <button className="modal-close" onClick={closeEnquiryModal}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            
                            <form onSubmit={handleEnquirySubmit} className="enquiry-form">
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label htmlFor="location">
                                            <i className="fas fa-map-marker-alt"></i>
                                            Location
                                        </label>
                                        <input
                                            type="text"
                                            id="location"
                                            name="location"
                                            value={enquiryForm.location}
                                            onChange={handleEnquiryFormChange}
                                            placeholder="Enter shoot location"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>
                                            <i className="fas fa-calendar-alt"></i>
                                            Preferred Date
                                        </label>
                                        <DatePicker
                                            selected={enquiryForm.preferredDate}
                                            onChange={handleDateChange}
                                            minDate={new Date()}
                                            dateFormat="MMMM d, yyyy"
                                            className="date-picker"
                                            placeholderText="Select date"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="budget">
                                            <i className="fas fa-rupee-sign"></i>
                                            Budget Range
                                        </label>
                                        <select
                                            id="budget"
                                            name="budget"
                                            value={enquiryForm.budget}
                                            onChange={handleEnquiryFormChange}
                                            required
                                        >
                                            <option value="">Select budget range</option>
                                            <option value="under-25k">Under ₹25,000</option>
                                            <option value="25k-50k">₹25,000 - ₹50,000</option>
                                            <option value="50k-1l">₹50,000 - ₹1,00,000</option>
                                            <option value="1l-2l">₹1,00,000 - ₹2,00,000</option>
                                            <option value="above-2l">Above ₹2,00,000</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="typeOfShoot">
                                            <i className="fas fa-camera"></i>
                                            Type of Shoot
                                        </label>
                                        <select
                                            id="typeOfShoot"
                                            name="typeOfShoot"
                                            value={enquiryForm.typeOfShoot}
                                            onChange={handleEnquiryFormChange}
                                            required
                                        >
                                            <option value="">Select shoot type</option>
                                            <option value="wedding">Wedding Photography</option>
                                            <option value="portrait">Portrait Photography</option>
                                            <option value="event">Event Photography</option>
                                            <option value="fashion">Fashion Photography</option>
                                            <option value="product">Product Photography</option>
                                            <option value="corporate">Corporate Photography</option>
                                            <option value="nature">Nature Photography</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="timeOfDay">
                                            <i className="fas fa-clock"></i>
                                            Time of Day
                                        </label>
                                        <select
                                            id="timeOfDay"
                                            name="timeOfDay"
                                            value={enquiryForm.timeOfDay}
                                            onChange={handleEnquiryFormChange}
                                            required
                                        >
                                            <option value="">Select time</option>
                                            <option value="morning">Morning (6 AM - 12 PM)</option>
                                            <option value="afternoon">Afternoon (12 PM - 6 PM)</option>
                                            <option value="evening">Evening (6 PM - 10 PM)</option>
                                            <option value="flexible">Flexible</option>
                                        </select>
                                    </div>

                                    <div className="form-group form-group-full">
                                        <label htmlFor="message">
                                            <i className="fas fa-edit"></i>
                                            Short Message (Optional)
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={enquiryForm.message}
                                            onChange={handleEnquiryFormChange}
                                            placeholder="Any specific requirements or details..."
                                            maxLength={150}
                                            rows={3}
                                        />
                                        <div className="character-count">
                                            {enquiryForm.message.length}/150 characters
                                        </div>
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button 
                                        type="button" 
                                        className="btn-secondary"
                                        onClick={closeEnquiryModal}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn-primary"
                                        disabled={isSubmittingEnquiry}
                                    >
                                        {isSubmittingEnquiry ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin"></i>
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-paper-plane"></i>
                                                Send Enquiry
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Success Modal */}
                {showSuccessModal && (
                    <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <div className="success-icon">
                                    <i className="fas fa-check-circle"></i>
                                </div>
                                <button className="modal-close" onClick={() => setShowSuccessModal(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            
                            <div className="modal-body">
                                <h2>Request Sent Successfully!</h2>
                                <p className="success-message">
                                    We've shared your booking request with <strong>{photographerData.name}</strong>.<br/>
                                    They usually respond within <strong>1–2 hours</strong> via WhatsApp or phone call.
                                </p>
                                
                                <div className="contact-options">
                                    <h3>Don't want to wait?</h3>
                                    <p>You can also contact them directly now:</p>
                                    
                                    <div className="contact-buttons">
                                        <button 
                                            className="whatsapp-btn"
                                            onClick={() => {
                                                if (photographerData.phoneNumber) {
                                                    window.open(`https://wa.me/${photographerData.phoneNumber.replace(/[^0-9]/g, '')}`, '_blank');
                                                } else {
                                                    toast.info('WhatsApp number not available');
                                                }
                                            }}
                                        >
                                            <i className="fab fa-whatsapp"></i>
                                            Message on WhatsApp
                                        </button>
                                        
                                        <button 
                                            className="call-btn"
                                            onClick={() => {
                                                if (photographerData.phoneNumber) {
                                                    window.open(`tel:${photographerData.phoneNumber}`);
                                                } else {
                                                    toast.info('Phone number not available');
                                                }
                                            }}
                                        >
                                            <i className="fas fa-phone"></i>
                                            Call Photographer Now
                                        </button>
                                    </div>
                                    
                                    <div className="tip-section">
                                        <p className="tip">
                                            <i className="fas fa-lightbulb"></i>
                                            <strong>Tip:</strong> Save their number to avoid missing their call.
                                        </p>
                                    </div>
                                </div>
                                
                                <button 
                                    className="btn-primary"
                                    onClick={() => setShowSuccessModal(false)}
                                >
                                    Got it, thanks!
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Album Photos Modal */}
                {showAlbumModal && selectedAlbum && (
                    <div className="modal-overlay" onClick={closeAlbumModal}>
                        <div className="modal-content album-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{selectedAlbum.albumName}</h2>
                                <button className="modal-close" onClick={closeAlbumModal}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="album-info">
                                <p><i className="fas fa-map-marker-alt"></i> {selectedAlbum.location}</p>
                                <p><i className="fas fa-images"></i> {selectedAlbum.photoCount} photos</p>
                                <p><i className="fas fa-tag"></i> {selectedAlbum.category}</p>
                            </div>
                            <div className="album-photos-grid">
                                {albumPhotos.length > 0 ? (
                                    albumPhotos.map((photo, index) => (
                                        <div key={index} className="album-photo-item">
                                            <img
                                                src={photo}
                                                alt={`${selectedAlbum.albumName} photo ${index + 1}`}
                                                className="album-photo"
                                                loading="lazy"
                                                decoding="async"
                                                onClick={() => window.open(photo, '_blank')}
                                                onError={(e) => {
                                                    e.target.src = 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg';
                                                }}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-photos">
                                        <i className="fas fa-image"></i>
                                        <p>No photos available in this album</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default PhotoProfile;
